import create from 'zustand'
import produce from 'immer'
import { DraftFunction } from 'use-immer'
import { TreeData, ItemId, mutateTree } from 'tree-dnd'
import { TreeDataItem, TreeItemType } from '../../FilesTree/types'
import { toTree } from 'components/FilesTree/treeUtils'
import { FavoritableType, Favorite } from 'api/clients/FavoriteClient'
import { ChannelType } from 'components/CampaignCreationModal/Types'

interface FileTreeState {
  tree: TreeData
  setTree: (treeOrFn: DraftFunction<TreeData> | TreeData) => void
  onExpand: (itemId: ItemId) => void
  onCollapse: (itemId: ItemId) => void
  appendChildren: (parentId: ItemId, childFiles: TreeDataItem[]) => void
  removeItem: (itemId: ItemId) => void
  updateItem: (itemId: ItemId, data: Partial<TreeDataItem>) => void
}

const RootItemID = '0'

const mapFavoritableTypeToTreeItemType = (favoritable_type: FavoritableType): TreeItemType => {
  if (favoritable_type === FavoritableType.Project) return TreeItemType.Project

  return favoritable_type.toLowerCase() as TreeItemType
}

export const mapFavoritesToTreeItems = (favorites: Favorite[]): TreeDataItem[] =>
  favorites.map((favorite, index) => ({
    id: favorite.id.toString(),
    file_id: favorite.favoritable_id || undefined,
    real_workspace_id: favorite.favoritable_workspace_id,
    type: mapFavoritableTypeToTreeItemType(favorite.favoritable_type),
    slug: favorite.favoritable_slug || undefined,
    title: favorite.favoritable_title,
    header_emoji: favorite.favoritable_emoji,
    handbook: favorite.favoritable_handbook,
    external_file_icon: favorite.favoritable_file_icon,
    channel: favorite.favoritable_channel as ChannelType,
    channel_emoji: favorite.favoritable_channel as ChannelType,
    has_edit_access: favorite.has_edit_access,
    url: favorite.favoritable_url,
    parent_id: favorite.favoritable_parent_id,
    ancestor_projects: [],
    is_root: false,
    handbook_mode: favorite.handbook_mode,
    database_mode: favorite.database_mode,
    default_project: favorite.default_project,
    default_view: favorite.default_view,
    has_descendant_articles: favorite.has_descendant_articles,
    has_subprojects: favorite.has_subprojects,
    isLast: index === favorites.length - 1,
    project_id: favorite.project_id,
  }))

const files2FavoriteTreeItems = (files: TreeDataItem[], tree: TreeData, parentId: ItemId) =>
  files.map((file: TreeDataItem) => {
    const itemAlreadyExist = tree.items[file.id]

    tree.items[file.id] = {
      id: file.id,
      children: itemAlreadyExist?.children || [],
      hasChildren: itemAlreadyExist?.children.length > 0,
      isExpanded: !!itemAlreadyExist?.isExpanded,
      data: { ...file, id: file.file_id || file.id },
    }

    return file.id
  })

export const useFavoritesTree = create<FileTreeState>((set, get) => ({
  tree: toTree([]),
  setTree: (treeOrFn) =>
    set((state) => ({ tree: typeof treeOrFn === 'function' ? produce(state.tree, treeOrFn) : treeOrFn })),
  onExpand: async (itemId: ItemId) => {
    const { tree, setTree } = get()
    setTree(mutateTree(tree, itemId, { isExpanded: true }))
  },
  onCollapse: async (itemId: ItemId) => {
    const { tree, setTree } = get()
    setTree(mutateTree(tree, itemId, { isExpanded: false }))
  },
  appendChildren: (parentId: ItemId, childFiles: TreeDataItem[]) => {
    set((state) => {
      const { tree } = state
      const expandedTreeItem = tree.items[parentId]

      if (childFiles && expandedTreeItem) {
        expandedTreeItem.children = files2FavoriteTreeItems(childFiles, tree, parentId)

        expandedTreeItem.isExpanded = true
        expandedTreeItem.hasChildren = !!childFiles.length
      }
    })
  },
  removeItem: (itemId: ItemId) => {
    set((state) => {
      const { tree } = state
      const item = Object.values(tree.items).find((i) => itemId === i.data.file_id)

      if (item) {
        const parent = tree.items[RootItemID]

        delete tree.items[item.id]
        parent.children = parent.children.filter((childId) => childId !== item.id)
      }
    })
  },
  updateItem: (itemId: ItemId, data: Partial<TreeDataItem>) => {
    set((state) => {
      const { tree } = state
      const item = tree.items[itemId]

      tree.items[itemId] = {
        ...item,
        data: {
          ...item.data,
          ...data,
        },
      }
    })
  },
}))
